'use client';

import { validateStock } from '@/utils/stock-validation';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

// Define types from the original CartContext
export interface CartItem {
    id: number;
    qty: number;
    price: number;
    promotionalPrice?: number;
    subtotal: number;
    discount?: number;
    total: number;
    purchasable: {
        id: number;
        title?: string;
        stock?: number;
        hasUnlimitedStock?: boolean;
        sku?: string;
        url?: string;
        slug?: string;
        product: {
            title: string;
            imageUrl?: string;
            url?: string;
        };
    };
}

export interface ShippingMethod {
    name: string;
    price: number;
}

export interface CustomerData {
    email: string;
    name: string;
    addressLine1: string;
    postalCode: string;
    city: string;
    countryCode: string;
    administrativeArea?: string;
}

interface GraphQLCart {
    number?: string;
    email?: string;
    isCompleted?: boolean;
    billingAddress?: {
        name?: string;
        addressLine1?: string;
        postalCode?: string;
        city?: string;
        countryCode?: string;
        administrativeArea?: string;
    };
    lineItems: Array<CartItem>;
    total?: number;
    totalShippingCost?: number;
    shippingMethod?: string;
    availableShippingMethods?: Array<{
        name?: string;
        price?: number;
    }>;
}

// Base persistent atoms
export const cartNumberAtom = atomWithStorage<string | null>('cartNumber', null);

// Base atoms
export const cartItemsAtom = atom<CartItem[]>([]);
export const customerDataAtom = atom<CustomerData | null>(null);
export const availableShippingMethodsAtom = atom<ShippingMethod[]>([]);
export const selectedShippingMethodAtom = atom<string | null>(null);
export const shippingCostAtom = atom<number>(0);
export const subtotalAtom = atom<number>(0);
export const isInitializedAtom = atom<boolean>(false);
export const isLoadingAtom = atom<boolean>(false);
export const errorAtom = atom<string | null>(null);

// Computed atoms
export const totalAtom = atom<number>((get) => get(subtotalAtom) + get(shippingCostAtom));

// Clear cart atom - resets all cart state and creates a new cart session
export const clearCartAtom = atom(null, async (_, set, siteId?: number) => {
    set(isLoadingAtom, true);
    set(errorAtom, null);

    try {
        // Clear all cart state
        set(cartNumberAtom, null);
        set(cartItemsAtom, []);
        set(customerDataAtom, null);
        set(availableShippingMethodsAtom, []);
        set(selectedShippingMethodAtom, null);
        set(shippingCostAtom, 0);
        set(subtotalAtom, 0);

        // Create a new empty cart session
        const response = await fetch('/api/cart', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ cartNumber: null, siteId }),
        });

        if (response.ok) {
            const data = await response.json();
            if (data.cart?.number) {
                set(cartNumberAtom, data.cart.number);
            }
        }
    } catch (error) {
        console.error('Error clearing cart:', error);
        // Even if API call fails, we still want local state cleared
    } finally {
        set(isLoadingAtom, false);
        set(isInitializedAtom, true);
    }
});

// Global flag for initialization (to prevent duplicate fetches)
let isInitializationInProgress = false;

// Interface for the setters object to avoid using 'any'
interface CartDataSetters {
    setCartNumber: (number: string) => void;
    setCustomerData: (data: CustomerData) => void;
    setAvailableShippingMethods: (methods: ShippingMethod[]) => void;
    setSelectedShippingMethod: (method: string) => void;
    setShippingCost: (cost: number) => void;
    setSubtotal: (subtotal: number) => void;
}

// Helper function to transform GraphQL cart data
const transformCartData = (cart: GraphQLCart | null, setters: CartDataSetters): CartItem[] => {
    if (!cart) {
        console.error('Cart is null in transformCartData');
        return [];
    }

    if (!cart.lineItems) {
        console.error('LineItems is missing in transformCartData');
        return [];
    }

    // Store cart number if available
    if (cart.number) {
        setters.setCartNumber(cart.number.toString());
    }

    // Store customer data if available
    if (cart.email || cart.billingAddress) {
        setters.setCustomerData({
            email: cart.email || '',
            name: cart.billingAddress?.name || '',
            addressLine1: cart.billingAddress?.addressLine1 || '',
            postalCode: cart.billingAddress?.postalCode || '',
            city: cart.billingAddress?.city || '',
            countryCode: cart.billingAddress?.countryCode || '',
            administrativeArea: cart.billingAddress?.administrativeArea || '',
        });
    }

    // Store shipping methods if available
    if (cart.availableShippingMethods && cart.availableShippingMethods.length > 0) {
        const methods = cart.availableShippingMethods
            .filter((method) => method?.name && method?.price !== undefined)
            .map((method) => ({
                name: method?.name || '',
                price: method?.price || 0,
            }));

        setters.setAvailableShippingMethods(methods);
    }

    // Store selected shipping method if available
    if (cart.shippingMethod) {
        setters.setSelectedShippingMethod(cart.shippingMethod);
    }

    // Store shipping cost
    if (cart.totalShippingCost !== undefined) {
        setters.setShippingCost(cart.totalShippingCost);
    }

    // Calculate and store subtotal
    const itemsSubtotal = cart.lineItems.reduce(
        (sum, item) => sum + (item.subtotal || item.price * item.qty),
        0
    );
    setters.setSubtotal(itemsSubtotal);

    // Transform and return line items
    return cart.lineItems.map((item) => ({
        id: item.id,
        qty: item.qty,
        price: item.price,
        promotionalPrice: item.promotionalPrice,
        subtotal: item.subtotal || item.price * item.qty,
        discount: item.discount,
        total: item.total,
        purchasable: {
            id: item.purchasable.id,
            title: item.purchasable.title,
            stock: item.purchasable.stock,
            hasUnlimitedStock: item.purchasable.hasUnlimitedStock,
            sku: item.purchasable.sku,
            slug: item.purchasable.slug,
            product: {
                title: item.purchasable.product.title,
                imageUrl: item.purchasable.product.imageUrl,
                url: item.purchasable.product.url,
            },
        },
    }));
};

// Initialization atom
export const initializeCartAtom = atom(null, async (get, set, siteId?: number) => {
    // Skip if already initialized or in progress
    if (get(isInitializedAtom) || isInitializationInProgress) {
        return;
    }

    isInitializationInProgress = true;
    set(isLoadingAtom, true);

    try {
        // Get cart number from storage
        const storedCartNumber = get(cartNumberAtom);

        const response = await fetch('/api/cart', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ cartNumber: storedCartNumber, siteId }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch cart');
        }

        if (data.cart) {
            // Check if the cart is completed (order has been placed)
            if (data.cart.isCompleted) {
                // Clear the completed cart from storage and state
                set(cartNumberAtom, null);
                set(cartItemsAtom, []);
                set(customerDataAtom, null);
                set(availableShippingMethodsAtom, []);
                set(selectedShippingMethodAtom, null);
                set(shippingCostAtom, 0);
                set(subtotalAtom, 0);

                // Initialize a new cart by calling the API without a cart number
                const newCartResponse = await fetch('/api/cart', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ cartNumber: null, siteId }),
                });

                const newCartData = await newCartResponse.json();

                if (newCartResponse.ok && newCartData.cart) {
                    const setters: CartDataSetters = {
                        setCartNumber: (number: string) => set(cartNumberAtom, number),
                        setCustomerData: (data: CustomerData) => set(customerDataAtom, data),
                        setAvailableShippingMethods: (methods: ShippingMethod[]) =>
                            set(availableShippingMethodsAtom, methods),
                        setSelectedShippingMethod: (method: string) =>
                            set(selectedShippingMethodAtom, method),
                        setShippingCost: (cost: number) => set(shippingCostAtom, cost),
                        setSubtotal: (subtotal: number) => set(subtotalAtom, subtotal),
                    };

                    const transformedItems = transformCartData(newCartData.cart, setters);
                    set(cartItemsAtom, transformedItems);
                }
            } else {
                // Cart is not completed, proceed normally
                const setters: CartDataSetters = {
                    setCartNumber: (number: string) => set(cartNumberAtom, number),
                    setCustomerData: (data: CustomerData) => set(customerDataAtom, data),
                    setAvailableShippingMethods: (methods: ShippingMethod[]) =>
                        set(availableShippingMethodsAtom, methods),
                    setSelectedShippingMethod: (method: string) =>
                        set(selectedShippingMethodAtom, method),
                    setShippingCost: (cost: number) => set(shippingCostAtom, cost),
                    setSubtotal: (subtotal: number) => set(subtotalAtom, subtotal),
                };

                const transformedItems = transformCartData(data.cart, setters);
                set(cartItemsAtom, transformedItems);
            }
        }
    } catch (error) {
        set(errorAtom, error instanceof Error ? error.message : 'Failed to fetch cart');
        console.error('Error fetching cart:', error);
    } finally {
        set(isInitializedAtom, true);
        set(isLoadingAtom, false);
        isInitializationInProgress = false;
    }
});

// Add item to cart atom
export const addItemAtom = atom(
    null,
    async (
        get,
        set,
        {
            purchasableId,
            quantity,
            siteId,
        }: { purchasableId: number; quantity: number; siteId?: number }
    ) => {
        // Skip if not initialized
        if (!get(isInitializedAtom)) return;

        set(isLoadingAtom, true);
        set(errorAtom, null);

        try {
            const cartNumber = get(cartNumberAtom);
            const currentItems = get(cartItemsAtom);

            // Client-side stock validation using centralized logic
            const existingItem = currentItems.find((item) => item.purchasable.id === purchasableId);
            const currentQty = existingItem ? existingItem.qty : 0;

            if (existingItem) {
                // Convert to Variant-like object for validation
                const variant = {
                    id: existingItem.purchasable.id,
                    name: existingItem.purchasable.title || '',
                    price: existingItem.price,
                    promotionalPrice: existingItem.promotionalPrice,
                    stock: existingItem.purchasable.stock,
                    hasUnlimitedStock: existingItem.purchasable.hasUnlimitedStock,
                    inStock: true,
                };

                const stockStatus = validateStock(
                    { variant, currentCartQuantity: currentQty, requestedQuantity: quantity },
                    {
                        outOfStock: 'Out of stock',
                        inCart: (params) => `${params.quantity} in cart`,
                        lastItemInCart: 'You have the last item already in your cart',
                        allItemsInCart: (params) =>
                            `You have all ${params.quantity} items already in your cart`,
                        insufficientStock: 'Cannot add more items - insufficient stock',
                        stockLimitExceededWithDetails: (params) =>
                            `Cannot add ${params.requested} items. Only ${params.available} in stock, and you already have ${params.inCart} in your cart.`,
                    }
                );

                if (!stockStatus.canAdd) {
                    set(errorAtom, stockStatus.errorMessage);
                    set(isLoadingAtom, false);
                    return;
                }
            }

            const response = await fetch('/api/cart/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    variantId: purchasableId,
                    qty: quantity,
                    cartNumber,
                    siteId,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                // Use the detailed error message from the API if available
                const errorMessage = data.message || 'An unknown error occurred. Please try again.';
                throw new Error(errorMessage);
            }

            if (data.success && data.cart) {
                // Update cart number if provided
                if (data.cart.number) {
                    set(cartNumberAtom, data.cart.number);
                }

                // Update cart items
                if (data.cart.lineItems) {
                    set(cartItemsAtom, data.cart.lineItems);
                }

                // Update subtotal
                if (data.cart.total !== undefined) {
                    set(subtotalAtom, data.cart.total);
                }

                // Update shipping methods if provided
                if (data.cart.availableShippingMethods) {
                    set(availableShippingMethodsAtom, data.cart.availableShippingMethods);
                }

                // Update selected shipping method if provided
                if (data.cart.shippingMethod) {
                    set(selectedShippingMethodAtom, data.cart.shippingMethod);
                }

                // Update shipping cost if provided
                if (data.cart.totalShippingCost !== undefined) {
                    set(shippingCostAtom, data.cart.totalShippingCost);
                }
            }
        } catch (error) {
            set(errorAtom, error instanceof Error ? error.message : 'Failed to add item to cart');
            console.error('Error adding item to cart:', error);
        } finally {
            set(isLoadingAtom, false);
        }
    }
);

// Remove item from cart atom
export const removeItemAtom = atom(
    null,
    async (
        get,
        set,
        {
            purchasableId,
            quantity,
            siteId,
        }: { purchasableId: number; quantity?: number; siteId?: number }
    ) => {
        // Skip if not initialized
        if (!get(isInitializedAtom)) return;

        set(isLoadingAtom, true);
        set(errorAtom, null);

        // Find the item in the cart
        const items = get(cartItemsAtom);
        const itemToRemove = items.find((item) => item.purchasable.id === purchasableId);

        if (!itemToRemove) {
            set(isLoadingAtom, false);
            return;
        }

        try {
            const cartNumber = get(cartNumberAtom);

            const response = await fetch('/api/cart/remove', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    lineItemId: itemToRemove.id,
                    qty: quantity,
                    cartNumber,
                    siteId,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to remove item from cart');
            }

            if (data.success && data.cart) {
                // Update cart number if provided
                if (data.cart.number) {
                    set(cartNumberAtom, data.cart.number);
                }

                // Update cart items
                if (data.cart.lineItems) {
                    set(cartItemsAtom, data.cart.lineItems);
                }

                // Update subtotal
                if (data.cart.total !== undefined) {
                    set(subtotalAtom, data.cart.total);
                }

                // Update shipping methods if provided
                if (data.cart.availableShippingMethods) {
                    set(availableShippingMethodsAtom, data.cart.availableShippingMethods);
                }

                // Update selected shipping method if provided
                if (data.cart.shippingMethod) {
                    set(selectedShippingMethodAtom, data.cart.shippingMethod);
                }

                // Update shipping cost if provided
                if (data.cart.totalShippingCost !== undefined) {
                    set(shippingCostAtom, data.cart.totalShippingCost);
                }
            }
        } catch (error) {
            set(
                errorAtom,
                error instanceof Error ? error.message : 'Failed to remove item from cart'
            );
            console.error('Error removing item from cart:', error);
        } finally {
            set(isLoadingAtom, false);
        }
    }
);

// Update customer data atom
export const updateCustomerAtom = atom(
    null,
    async (get, set, customerData: CustomerData & { siteId?: number }) => {
        // Skip if not initialized
        if (!get(isInitializedAtom)) return;

        set(isLoadingAtom, true);
        set(errorAtom, null);

        try {
            const cartNumber = get(cartNumberAtom);

            const response = await fetch('/api/cart/customer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...customerData,
                    cartNumber,
                    siteId: customerData.siteId,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update customer information');
            }

            // Update customer data in state
            set(customerDataAtom, customerData);

            if (data.success && data.cart) {
                // Process the response and update state
                const setters: CartDataSetters = {
                    setCartNumber: (number: string) => set(cartNumberAtom, number),
                    setCustomerData: (data: CustomerData) => set(customerDataAtom, data),
                    setAvailableShippingMethods: (methods: ShippingMethod[]) =>
                        set(availableShippingMethodsAtom, methods),
                    setSelectedShippingMethod: (method: string) =>
                        set(selectedShippingMethodAtom, method),
                    setShippingCost: (cost: number) => set(shippingCostAtom, cost),
                    setSubtotal: (subtotal: number) => set(subtotalAtom, subtotal),
                };

                if (data.cart.lineItems) {
                    const transformedItems = transformCartData(data.cart, setters);
                    set(cartItemsAtom, transformedItems);
                }
            }

            return data.cart;
        } catch (error) {
            set(
                errorAtom,
                error instanceof Error ? error.message : 'Failed to update customer information'
            );
            console.error('Error updating customer information:', error);
            throw error;
        } finally {
            set(isLoadingAtom, false);
        }
    }
);

// Select shipping method atom
export const selectShippingMethodAtom = atom(
    null,
    async (get, set, { methodName, siteId }: { methodName: string; siteId?: number }) => {
        // Skip if not initialized or no cart number
        const cartNumber = get(cartNumberAtom);
        if (!get(isInitializedAtom) || !cartNumber) return;

        set(isLoadingAtom, true);
        set(errorAtom, null);

        try {
            const response = await fetch('/api/cart/shipping-method', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cartNumber,
                    shippingMethod: methodName,
                    siteId,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to update shipping method');
            }

            // Update shipping method in state
            set(selectedShippingMethodAtom, methodName);

            if (data.success && data.cart) {
                // Process the response and update state
                const setters: CartDataSetters = {
                    setCartNumber: (number: string) => set(cartNumberAtom, number),
                    setCustomerData: (data: CustomerData) => set(customerDataAtom, data),
                    setAvailableShippingMethods: (methods: ShippingMethod[]) =>
                        set(availableShippingMethodsAtom, methods),
                    setSelectedShippingMethod: (method: string) =>
                        set(selectedShippingMethodAtom, method),
                    setShippingCost: (cost: number) => set(shippingCostAtom, cost),
                    setSubtotal: (subtotal: number) => set(subtotalAtom, subtotal),
                };

                if (data.cart.lineItems) {
                    const transformedItems = transformCartData(data.cart, setters);
                    set(cartItemsAtom, transformedItems);
                }

                // Ensure shipping cost is updated
                if (data.cart.totalShippingCost !== undefined) {
                    set(shippingCostAtom, data.cart.totalShippingCost);
                }
            }

            return data.cart;
        } catch (error) {
            set(
                errorAtom,
                error instanceof Error ? error.message : 'Failed to update shipping method'
            );
            console.error('Error updating shipping method:', error);
            throw error;
        } finally {
            set(isLoadingAtom, false);
        }
    }
);

// Create order atom
export const createOrderAtom = atom(
    null,
    async (
        get,
        set,
        { returnUrl, cancelUrl, siteId }: { returnUrl: string; cancelUrl: string; siteId?: number }
    ) => {
        set(isLoadingAtom, true);
        set(errorAtom, null);

        try {
            const cartNumber = get(cartNumberAtom);

            const response = await fetch('/api/cart/create-order', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cartNumber,
                    returnUrl,
                    cancelUrl,
                    siteId,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to create order');
            }

            return data.order;
        } catch (error) {
            console.error('Error creating order:', error);
            set(errorAtom, error instanceof Error ? error.message : 'Failed to create order');
            throw error;
        } finally {
            set(isLoadingAtom, false);
        }
    }
);
